// Workspace-wide Referrals grid — mirrors DealCloud's "Referrals Received"
// dashboard.
//
// One row per referred contact, enriched server-side (/api/referrals) with the
// referred company's financials/industry and its latest engagement (stage,
// next action, fees), so the grid carries the same information density as the
// DealCloud view. Grouped like DealCloud: by Referral Maker (the person who
// made the intro — DC's primary grid) or by Source Company (DC's second grid),
// or flat.
//
// Wording is deliberately plain: no source-kind jargon ("partial"/"external")
// — a referral either names who made it / where they work, or the cell shows
// "No source on file".

// ONE column catalog — the Columns menu, width fitting, and exports all read
// it (same contract as the other catalog grids; the row itself renders via
// ReferralGridRow's per-key switch because its cells need view callbacks).
// `accessor` is the display text sampled for width fitting; `xget`/`xkey`
// keep the export exactly what it always was: the raw row fields under their
// original names (ISO dates, unformatted numbers).
const REFERRAL_COLUMNS = [
  { key: 'company',        label: 'Company',        xkey: 'company_name',
    accessor: (r) => r.company_name, xget: (r) => r.company_name },
  { key: 'contact',        label: 'Contact',        xkey: 'full_name', fixed: true,
    accessor: (r) => r.full_name, xget: (r) => r.full_name },
  { key: 'date',           label: 'Referral Date',  xkey: 'referral_date',
    accessor: () => 'Sep 22, 2025', xget: (r) => r.referral_date },
  { key: 'maker',          label: 'Referral Maker', xkey: 'referral_maker',
    accessor: (r) => r.referral_maker, xget: (r) => r.referral_maker },
  { key: 'source_company', label: 'Source Company', xkey: 'referral_source_company_name',
    accessor: (r) => r.referral_source_company_name, xget: (r) => r.referral_source_company_name },
  { key: 'owner',          label: 'Referral Owner', xkey: 'referral_owner_name',
    accessor: (r) => r.referral_owner_name, xget: (r) => r.referral_owner_name },
  { key: 'gpp',            label: 'GPP',            xkey: 'referral_is_gpp',
    accessor: () => 'Yes', xget: (r) => r.referral_is_gpp,
    desc: 'The referral came from a Growth Partners Program partner.' },
  { key: 'stage',          label: 'Stage',          xkey: 'stage_name',
    accessor: (r) => r.stage_name, xget: (r) => r.stage_name },
  { key: 'next_action',    label: 'Next Action',    xkey: 'next_action_notes',
    accessor: (r) => (r.next_action_notes ? r.next_action_notes.slice(0, 40) : null),
    xget: (r) => r.next_action_notes },
  { key: 'fees',           label: 'Est. Fees',      xkey: 'estimated_fees', align: 'right',
    accessor: (r) => (r.estimated_fees != null ? window.fmtMoney(r.estimated_fees) : null),
    xget: (r) => r.estimated_fees },
  { key: 'revenue',        label: 'Revenue',        xkey: 'revenue_est', align: 'right',
    accessor: (r) => (r.revenue_est != null ? window.fmtMoney(r.revenue_est) : null),
    xget: (r) => r.revenue_est },
  { key: 'ebitda',         label: 'EBITDA',         xkey: 'ebitda_est', align: 'right',
    accessor: (r) => (r.ebitda_est != null ? window.fmtMoney(r.ebitda_est) : null),
    xget: (r) => r.ebitda_est },
  { key: 'industry',       label: 'Industry',       xkey: 'industry_name',
    accessor: (r) => r.industry_name, xget: (r) => r.industry_name },
  { key: 'lead_source',    label: 'Lead Source',    xkey: 'lead_source',
    accessor: (r) => r.lead_source, xget: (r) => r.lead_source },
  { key: 'created',        label: 'Created',        xkey: 'dc_created_at',
    accessor: (r) => window.fmtDate(window.recordCreated(r)), xget: (r) => r.dc_created_at },
  { key: 'updated',        label: 'Updated',        xkey: 'dc_modified_at',
    accessor: (r) => window.relativeTime(window.recordModified(r)), xget: (r) => r.dc_modified_at },
];

const REFERRAL_SORT_EXTRACT = {
  company:        (r) => (r.company_name || '').toLowerCase() || null,
  contact:        (r) => (r.full_name || '').toLowerCase() || null,
  date:           (r) => r.referral_date || null,
  maker:          (r) => (r.referral_maker || '').toLowerCase() || null,
  source_company: (r) => (r.referral_source_company_name || '').toLowerCase() || null,
  owner:          (r) => (r.referral_owner_name || '').toLowerCase() || null,
  gpp:            (r) => (r.referral_is_gpp ? 1 : 0),
  stage:          (r) => (r.stage_name || '').toLowerCase() || null,
  next_action:    (r) => r.next_action_date || null,
  fees:           (r) => r.estimated_fees,
  revenue:        (r) => r.revenue_est,
  ebitda:         (r) => r.ebitda_est,
  industry:       (r) => (r.industry_name || '').toLowerCase() || null,
  lead_source:    (r) => (r.lead_source || '').toLowerCase() || null,
  // The referred contact's DealCloud record dates (see data.jsx).
  ...window.recordDateSort(),
};

const REFERRALS_OWNER_SCOPE = window.makeOwnerScope('atlas:referrals-owner-scope');

const ReferralsView = ({ setContactId, setCompanyId, setDealId, activeUser }) => {
  const [items, setItems] = React.useState(null);
  const [total, setTotal] = React.useState(null);  // true match count (rows can be capped by the server's limit)
  const [users, setUsers] = React.useState([]);
  const [recording, setRecording] = React.useState(false);  // "New referral" drawer
  const [groupBy, setGroupBy] = React.useState(
    () => localStorage.getItem('atlas.referrals.groupBy') || 'maker'
  );
  const [sortKey, setSortKey] = React.useState('date');
  const [sortDir, setSortDir] = React.useState('desc');
  const toggleSort = React.useMemo(
    () => window.makeToggleSort(setSortKey, setSortDir), []
  );
  // "Mine only" always defaulted on here — now the opt-out sticks (and
  // cross-team roles start unchecked), same personalization as Referral
  // Partners / GPP; see makeOwnerScope.
  const [filters, setFilters] = React.useState(() => ({
    ownerId:  '',
    onlyGpp:  false,
    since:    '',
    until:    '',
    onlyMine: REFERRALS_OWNER_SCOPE.get(activeUser?.role) === 'mine',
  }));
  // activeUser can arrive after mount on a cold boot. Only the scope-derived
  // default may move the checkbox — once it (or Clear) has been touched,
  // whatever is on screen wins.
  const ownerTouched = React.useRef(false);
  React.useEffect(() => {
    if (ownerTouched.current || !activeUser?.id) return;
    setFilters((f) => {
      const want = REFERRALS_OWNER_SCOPE.get(activeUser.role) === 'mine';
      return (f.onlyMine === want || f.ownerId) ? f : { ...f, onlyMine: want };
    });
  }, [activeUser?.id, activeUser?.role]);

  React.useEffect(() => {
    window.AtlasApi.listTeamMembers()
      .then((m) => setUsers(Array.isArray(m) ? m.filter((u) => u.active !== false) : []))
      .catch(() => setUsers([]));
  }, []);

  React.useEffect(() => {
    if (filters.onlyMine && !activeUser) {
      setFilters((f) => ({ ...f, onlyMine: false }));
    }
  }, [activeUser, filters.onlyMine]);

  React.useEffect(() => {
    try { localStorage.setItem('atlas.referrals.groupBy', groupBy); } catch (_) {}
  }, [groupBy]);

  // Inline cell editing — shared EditableCell primitive (same as the Companies
  // grid). onSave PATCHes the entity, then merges the server's response back
  // into the row so denormalized labels (owner name, resolved maker) stay
  // consistent without a full reload.
  const saveContact = React.useCallback(async (id, patch) => {
    const u = await window.AtlasApi.updateContact(id, patch);
    setItems((prev) => (prev || []).map((x) => (x.id !== id ? x : {
      ...x,
      referral_date: u.referral_date,
      referral_owner_id: u.referral_owner_id,
      referral_owner_name: u.referral_owner_name,
      referral_is_gpp: u.referral_is_gpp,
      // Same precedence as the server: resolved source contact wins over the
      // free-text ReferralMaker.
      referral_maker: u.referral_source_contact_name || u.referral_maker,
    })));
  }, []);
  const saveEngagement = React.useCallback(async (engId, patch) => {
    const u = await window.AtlasApi.updateEngagement(engId, patch);
    setItems((prev) => (prev || []).map((x) => (x.engagement_id !== engId ? x : {
      ...x,
      next_action_notes: u.next_action_notes,
      next_action_date: u.next_action_date,
    })));
  }, []);

  const reload = React.useCallback(() => {
    setItems(null);
    const params = {};
    const effectiveOwner = filters.ownerId || (filters.onlyMine && activeUser ? activeUser.id : '');
    if (effectiveOwner) params.referral_owner_id = effectiveOwner;
    if (filters.onlyGpp) params.is_gpp = true;
    if (filters.since) params.since = new Date(filters.since).toISOString();
    if (filters.until) params.until = new Date(filters.until).toISOString();
    window.AtlasApi.listReferrals(params)
      .then((res) => { setItems(res?.items || []); setTotal(res?.total ?? null); })
      .catch(() => { setItems([]); setTotal(null); });
  }, [filters, activeUser]);
  React.useEffect(() => { reload(); }, [reload]);

  // Sort rows, then bucket into groups (groups sorted A→Z, like DealCloud).
  const sorted = React.useMemo(() => {
    const rows = items || [];
    return [...rows].sort(window.makeComparator(sortKey, sortDir, REFERRAL_SORT_EXTRACT));
  }, [items, sortKey, sortDir]);

  const groups = React.useMemo(() => {
    if (groupBy === 'none') return [{ name: null, rows: sorted }];
    const keyOf = groupBy === 'maker'
      ? (r) => r.referral_maker || 'No referral maker on file'
      : (r) => r.referral_source_company_name || 'No source company on file';
    const map = new Map();
    for (const r of sorted) {
      const k = keyOf(r);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(r);
    }
    return [...map.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([name, rows]) => ({ name, rows }));
  }, [sorted, groupBy]);

  const kpis = React.useMemo(() => {
    const rows = items || [];
    return {
      total:      rows.length,
      engaged:    rows.filter((r) => r.stage_name).length,
      gpp:        rows.filter((r) => r.referral_is_gpp).length,
      noSource:   rows.filter((r) => !r.referral_maker && !r.referral_source_company_name).length,
    };
  }, [items]);

  // Per-user column layout (105) — show/hide/reorder via the Columns menu.
  // Widths dragged before the Columns button existed carry over automatically
  // (useColumnPrefs reads the old 'atlas.colw.referrals' blob as a fallback).
  const columns = window.useColumnPrefs('referrals', REFERRAL_COLUMNS, {
    rows: items || [], fitMax: 300,
  });

  // Export mirrors the grid: the columns you can see, in your order and sort.
  // Values and field names are unchanged from the old fixed export (raw row
  // fields via each column's xget/xkey), plus the Atlas contact id for joins.
  const doExport = (format) => {
    const xcols = window.exportColumnsFor(columns.cols,
      [{ key: 'id', label: 'Atlas contact id', get: (r) => r.id }]);
    const records = window.exportRecords(xcols, sorted);
    const name = `referrals-${window.localISODate()}`;
    if (format === 'csv') window.exportCsv(`${name}.csv`, xcols, records);
    else window.exportJson(`${name}.json`, records);
  };

  const groupLabel = { maker: 'Referral Maker', source: 'Source Company', none: 'None' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PageHeader
        breadcrumb={['Workspace', 'Referrals']}
        title="Referrals"
        meta={items === null ? 'Loading…'
          : total != null && items.length < total
            ? `showing ${items.length.toLocaleString()} of ${total.toLocaleString()} referred contacts — narrow the date filter`
            : `${items.length.toLocaleString()} referred contacts`}
        actions={(
          <>
            <Btn size="sm" variant="accent" icon={<Icon.Plus size={11} />} onClick={() => setRecording(true)}>
              New referral
            </Btn>
            <window.ExportMenu
              disabled={!items || !items.length}
              viewCount={sorted.length}
              onExport={doExport}
            />
            <window.ColumnsMenu
              catalog={REFERRAL_COLUMNS}
              cols={columns.cols}
              toggle={columns.toggle}
              move={columns.move}
              reset={columns.reset}
            />
          </>
        )}
      />

      <div style={{ flex: 1, overflow: 'auto', background: 'var(--surface-2)', padding: 16 }}>
        {/* Filter bar */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)',
                       borderRadius: 8, padding: 10, marginBottom: 14,
                       display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-2)' }}>
            <input type="checkbox" checked={filters.onlyMine} disabled={!activeUser}
              onChange={(e) => {
                ownerTouched.current = true;
                REFERRALS_OWNER_SCOPE.set(e.target.checked ? 'mine' : 'any');
                setFilters((f) => ({ ...f, onlyMine: e.target.checked, ownerId: '' }));
              }} />
            Mine only
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 11, color: 'var(--text-3)' }}>Owner:</span>
            <Combobox
              value={filters.ownerId}
              onChange={(e) => {
                const v = e.target.value;
                ownerTouched.current = true;
                // Explicit "Any owner" / self picks persist; a colleague
                // stays a one-visit peek.
                if (v === '') REFERRALS_OWNER_SCOPE.set('any');
                else if (activeUser?.id && v === activeUser.id) REFERRALS_OWNER_SCOPE.set('mine');
                setFilters((f) => ({ ...f, ownerId: v, onlyMine: false }));
              }}
              options={[
                { value: '', label: 'Any owner' },
                ...users.map((u) => ({ value: u.id, label: u.full_name })),
              ]}
              style={{ minWidth: 180 }}
            />
          </div>
          <label title="Only referrals whose source is a Growth Partners Program partner"
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-2)' }}>
            <input type="checkbox" checked={filters.onlyGpp}
              onChange={(e) => setFilters((f) => ({ ...f, onlyGpp: e.target.checked }))} />
            GPP only
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 11, color: 'var(--text-3)' }}>Since:</span>
            <TextInput type="date" value={filters.since}
              onChange={(e) => setFilters((f) => ({ ...f, since: e.target.value }))}
              style={{ width: 140 }} />
            <span style={{ fontSize: 11, color: 'var(--text-3)' }}>Until:</span>
            <TextInput type="date" value={filters.until}
              onChange={(e) => setFilters((f) => ({ ...f, until: e.target.value }))}
              style={{ width: 140 }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 11, color: 'var(--text-3)' }}>Group by:</span>
            {['maker', 'source', 'none'].map((g) => (
              <button key={g} onClick={() => setGroupBy(g)} style={{
                padding: '4px 10px', borderRadius: 5, fontSize: 11.5, fontWeight: 500,
                cursor: 'pointer', fontFamily: 'inherit',
                background: groupBy === g ? 'var(--accent-soft)' : 'var(--surface-2)',
                color:      groupBy === g ? 'var(--accent-text)' : 'var(--text-2)',
                border:     groupBy === g ? '1px solid var(--accent-text)' : '1px solid var(--border)',
              }}>{groupLabel[g]}</button>
            ))}
          </div>
          <div style={{ flex: 1 }} />
          <Btn size="xs" variant="ghost"
            onClick={() => {
              // Clear is a one-visit "show me everything" — it doesn't
              // change the remembered owner default.
              ownerTouched.current = true;
              setFilters({ ownerId: '', onlyGpp: false, since: '', until: '', onlyMine: false });
            }}>
            Clear filters
          </Btn>
        </div>

        {/* KPI strip */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 14 }}>
          {[
            { label: 'Referrals', value: kpis.total,
              hint: 'Referred contacts matching the current filters' },
            { label: 'With engagement', value: kpis.engaged,
              hint: 'The referred company has an engagement in the pipeline' },
            { label: 'GPP partner', value: kpis.gpp,
              hint: 'The referral came from a Growth Partners Program partner' },
            { label: 'No source on file', value: kpis.noSource,
              hint: 'Nobody is recorded as having made the intro — worth backfilling in DealCloud' },
          ].map((k) => (
            <div key={k.label} title={k.hint}
              style={{ background: 'var(--surface)', border: '1px solid var(--border)',
                        borderRadius: 6, padding: '8px 12px', cursor: 'default' }}>
              <div style={{ fontSize: 10.5, color: 'var(--text-4)', textTransform: 'uppercase',
                             letterSpacing: '0.05em', fontWeight: 500 }}>{k.label}</div>
              <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)' }}>
                {items === null ? '…' : k.value.toLocaleString()}
              </div>
            </div>
          ))}
        </div>

        {/* Grid */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)',
                       borderRadius: 8, overflow: 'auto' }}>
          {items === null && (
            <div style={{ padding: 24, fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>
          )}
          {items && items.length === 0 && (
            <div style={{ padding: 24, fontSize: 12, color: 'var(--text-3)' }}>
              {filters.onlyMine ? (
                <>
                  No referrals assigned to you.{' '}
                  <button
                    onClick={() => setFilters((f) => ({ ...f, onlyMine: false }))}
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                      color: 'var(--accent-text)', font: 'inherit', textDecoration: 'underline' }}>
                    Show all referrals
                  </button>
                  {' '}to see the whole workspace.
                </>
              ) : 'No referrals match the current filters.'}
            </div>
          )}
          {items && items.length > 0 && (
            <div style={{ minWidth: 'max-content' }}>
              {/* Header */}
              <div style={{ display: 'flex', height: 30, background: 'var(--surface-2)',
                             position: 'sticky', top: 0, zIndex: 1,
                             borderBottom: '1px solid var(--border)' }}>
                {columns.cols.map((c) => (
                  <window.SortableHeader key={c.key} col={c}
                    sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}
                    sortable={Boolean(REFERRAL_SORT_EXTRACT[c.key])}
                    onResizeStart={columns.startResize(c.key)}
                  />
                ))}
              </div>
              {groups.map((g) => (
                <React.Fragment key={g.name ?? '__all__'}>
                  {g.name != null && (
                    <div style={{ padding: '6px 12px', fontSize: 11, fontWeight: 600,
                                   color: 'var(--text-2)', background: 'var(--surface-2)',
                                   borderBottom: '1px solid var(--border)',
                                   position: 'sticky', left: 0 }}>
                      {g.name}
                      <span style={{ fontWeight: 400, color: 'var(--text-4)', marginLeft: 6 }}>
                        {g.rows.length} referral{g.rows.length === 1 ? '' : 's'}
                      </span>
                    </div>
                  )}
                  {g.rows.map((r) => (
                    <ReferralGridRow key={r.id} r={r} cols={columns.cols} users={users}
                      onOpenContact={() => setContactId(r.id)}
                      onOpenCompany={r.company_id ? () => setCompanyId(r.company_id) : null}
                      onOpenMaker={r.referral_source_contact_id ? () => setContactId(r.referral_source_contact_id) : null}
                      onOpenSourceCompany={r.referral_source_company_id ? () => setCompanyId(r.referral_source_company_id) : null}
                      onOpenDeal={r.engagement_id && setDealId ? () => setDealId(r.engagement_id) : null}
                      onSaveContact={(patch) => saveContact(r.id, patch)}
                      onSaveEngagement={r.engagement_id ? (patch) => saveEngagement(r.engagement_id, patch) : null}
                    />
                  ))}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      </div>

      {recording && (
        <NewReferralDrawer
          users={users}
          activeUser={activeUser}
          onClose={() => setRecording(false)}
          onCreated={() => { setRecording(false); reload(); }}
        />
      )}
    </div>
  );
};

// Server-backed contact search — the shared Combobox filters a fixed option
// list client-side, which doesn't work for 10k contacts. Type 2+ chars,
// pick a result; the picked contact shows as a removable chip.
// `createType` (a contact_type) turns on "+ New contact" under the results:
// it opens NewPersonDrawer seeded with the query, so a person who isn't in
// Atlas yet can be created without leaving the form.
const ContactPicker = ({ value, onPick, placeholder, createType }) => {
  const [q, setQ] = React.useState('');
  const [results, setResults] = React.useState([]);
  const [creating, setCreating] = React.useState(false);

  React.useEffect(() => {
    const t = q.trim();
    if (t.length < 2) { setResults([]); return; }
    const h = setTimeout(() => {
      window.AtlasApi.listContacts({ q: t, limit: 15 })
        .then((r) => setResults((r && r.items) || []))
        .catch(() => setResults([]));
    }, 200);
    return () => clearTimeout(h);
  }, [q]);

  if (value) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <Pill hue="teal" sz="sm">👤 {value.full_name}{value.company_name ? ` · ${value.company_name}` : ''}</Pill>
        <button onClick={() => onPick(null)} title="Clear"
          style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-3)', fontSize: 12, padding: 0 }}>
          ✕
        </button>
      </div>
    );
  }
  const showCreate = createType && q.trim().length >= 2;
  return (
    <div>
      <TextInput value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder || 'Search contacts…'} />
      {(results.length > 0 || showCreate) && (
        <div style={{ border: '1px solid var(--border)', borderRadius: 6, marginTop: 4, maxHeight: 180, overflowY: 'auto' }}>
          {results.map((c) => (
            <button key={c.id} onClick={() => { onPick(c); setQ(''); setResults([]); }}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px',
                       background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 12.5 }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-2)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
              {c.full_name}
              {c.company_name && <span style={{ color: 'var(--text-3)', fontSize: 11.5 }}> · {c.company_name}</span>}
            </button>
          ))}
          {showCreate && (
            <button onClick={() => setCreating(true)}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px',
                       background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 12.5,
                       color: 'var(--accent-text)', fontWeight: 500,
                       borderTop: results.length ? '1px solid var(--border)' : 'none' }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-2)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
              + New contact “{q.trim()}”
            </button>
          )}
        </div>
      )}
      {/* Portaled to body: the picker lives inside a <Field> label, and a
          drawer nested in a label steals clicks/focus back to the input. */}
      {creating && ReactDOM.createPortal(
        <window.NewPersonDrawer
          initialName={q.trim()}
          initialType={createType}
          onClose={() => setCreating(false)}
          onCreated={(contact) => {
            setCreating(false);
            if (contact?.id) { onPick(contact); setQ(''); setResults([]); }
            window.dispatchEvent(new CustomEvent('atlas-data-changed'));
          }}
        />, document.body)}
    </div>
  );
};

// Record a referral the way DealCloud does: it's not a new record, it's the
// referral block on an existing contact. Pick who got referred, who made the
// intro (their employer becomes the source company), the owning dealmaker,
// and whether the source is a GPP partner. Both pickers can create the
// person (and their company) in place if they aren't in Atlas yet.
// `initialSource` presets the referral maker — the contact page's
// "Log referral" button passes the contact being viewed; `initialIsGpp`
// pre-ticks the GPP box when that person is a GPP partner.
// `zIndex` (optional) lifts the drawer over an open reference drawer — the v2
// contact page passes it; the default is the Drawer's own.
const NewReferralDrawer = ({ users, activeUser, onClose, onCreated, initialSource, zIndex, initialIsGpp = false }) => {
  const [contact, setContact]   = React.useState(null);   // the referred contact
  const [source, setSource]     = React.useState(initialSource || null);   // who made the intro
  const [date, setDate]         = React.useState(() => window.localISODate());
  const [ownerId, setOwnerId]   = React.useState(activeUser?.id || '');
  const [isGpp, setIsGpp]       = React.useState(!!initialIsGpp);
  const [notes, setNotes]       = React.useState('');
  const [saving, setSaving]     = React.useState(false);

  const canSave = contact && date && !saving;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const patch = {
        referral_date: date,
        referral_owner_id: ownerId || null,
        referral_is_gpp: isGpp,
        referral_notes: notes.trim() || null,
      };
      if (source) {
        patch.referral_source_kind = 'external';
        patch.referral_source_contact_id = source.id;
        // The source contact's employer is the "source company" (the Raymond
        // James / Edward Jones column on the grid).
        if (source.company_id) patch.referral_source_company_id = source.company_id;
      }
      await window.AtlasApi.updateContact(contact.id, patch);
      pushToast({ type: 'success', title: 'Referral recorded', body: contact.full_name });
      onCreated();
    } catch (e) {
      pushToast({ type: 'error', title: 'Save failed', body: e.message });
    } finally { setSaving(false); }
  };

  return (
    <Drawer
      open={true}
      onClose={onClose}
      title="New referral"
      zIndex={zIndex}
      footer={(
        <>
          <Btn size="sm" variant="outline" onClick={onClose} disabled={saving}>Cancel</Btn>
          <Btn size="sm" variant="accent" onClick={save} disabled={!canSave}>
            {saving ? 'Saving…' : 'Record referral'}
          </Btn>
        </>
      )}
    >
      <Field label="Referred contact" hint="Who got introduced to IndustryPro. If they're new, type their name and create them right here.">
        <ContactPicker value={contact} onPick={setContact} placeholder="Search contacts…" createType="seller" />
      </Field>
      <Field label="Referral maker" hint="Optional — who made the intro. Their company becomes the source company.">
        <ContactPicker value={source} onPick={setSource} placeholder="Search contacts…" createType="professional" />
      </Field>
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ flex: 1 }}>
          <Field label="Referral date">
            <TextInput type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </Field>
        </div>
        <div style={{ flex: 1 }}>
          <Field label="Referral owner">
            <Combobox value={ownerId} onChange={(e) => setOwnerId(e.target.value)}
              options={[
                { value: '', label: '(unassigned)' },
                ...users.map((u) => ({ value: u.id, label: u.full_name })),
              ]} />
          </Field>
        </div>
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--text-2)', margin: '2px 0 10px' }}>
        <input type="checkbox" checked={isGpp} onChange={(e) => setIsGpp(e.target.checked)} />
        Referral came from a GPP partner
      </label>
      <Field label="Notes" hint="Optional context on the intro.">
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
          style={{ width: '100%', minHeight: 80, padding: '8px 10px', fontSize: 12.5,
                   border: '1px solid var(--border)', borderRadius: 6, fontFamily: 'inherit', resize: 'vertical' }} />
      </Field>
    </Drawer>
  );
};

const ReferralGridRow = ({
  r, cols, users, onOpenContact, onOpenCompany, onOpenMaker, onOpenSourceCompany,
  onOpenDeal, onSaveContact, onSaveEngagement,
}) => {
  const EditableCell = window.EditableCell;
  // `col` is the resolved catalog entry (w/last/align from useColumnPrefs) —
  // the body renders whatever columns the user has visible, in their order.
  const cell = (col, content, opts = {}) => (
    <div key={col.key}
      style={{ width: col.w, padding: '0 10px', fontSize: 12,
                   color: opts.color || 'var(--text-2)',
                   borderRight: col.last ? 'none' : '1px solid var(--border)',
                   display: 'flex', alignItems: 'center', overflow: 'hidden',
                   whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                   justifyContent: col.align === 'right' ? 'flex-end' : 'flex-start',
                   fontVariantNumeric: col.align === 'right' ? 'tabular-nums' : undefined }}
      title={opts.title}
      // Editable cells swallow the click so opening the editor (or clicking a
      // link inside it) doesn't also open the contact via the row onClick.
      onClick={opts.onClick ? (e) => { e.stopPropagation(); opts.onClick(); }
        : opts.edit ? (e) => e.stopPropagation() : undefined}
    >
      {opts.edit ? content : (
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis',
                        cursor: opts.onClick ? 'pointer' : undefined,
                        color: opts.onClick ? 'var(--accent-text)' : undefined }}>
          {content}
        </span>
      )}
    </div>
  );

  const nextAction = r.next_action_notes || (r.next_action_date ? window.fmtDate(r.next_action_date) : null);
  const nextActionFull = [
    r.next_action_date ? window.fmtDate(r.next_action_date) : null,
    r.next_action_notes,
  ].filter(Boolean).join(' — ');

  const renderCell = (c) => {
    switch (c.key) {
      case 'company': return cell(c, r.company_name || '—', {
        onClick: onOpenCompany, color: 'var(--text)',
        // The one-line business descriptor from DealCloud, where synced.
        title: r.pb_descriptor || undefined,
      });
      case 'contact': return cell(c, r.full_name || '—', { color: 'var(--text)', title: r.title || undefined });
      case 'date': return cell(c, (
        <EditableCell type="date" fill
          value={r.referral_date ? r.referral_date.slice(0, 10) : null}
          locked={r.dc_synced}
          onSave={(referral_date) => onSaveContact({ referral_date })} />
      ), { edit: true });
      case 'maker': return onOpenMaker ? (
        // Resolved source contact — a real Contact record; the name links to
        // their profile. Reassigning the source is done from the contact page.
        cell(c, r.referral_maker || '—', { onClick: onOpenMaker, title: 'Open referring contact' })
      ) : cell(c, (
        <EditableCell type="text" fill value={r.referral_maker}
          locked={r.dc_synced}
          onSave={(referral_maker) => onSaveContact({ referral_maker })} />
      ), { edit: true, title: r.referral_maker ? undefined : 'No source on file — nobody is recorded as having made this intro' });
      case 'source_company': return cell(c, r.referral_source_company_name || '—', { onClick: onOpenSourceCompany });
      case 'owner': return cell(c, (
        <EditableCell type="user" fill value={r.referral_owner_id} users={users || []}
          fallbackLabel={r.referral_owner_name}
          locked={r.dc_synced}
          onSave={(referral_owner_id) => onSaveContact({ referral_owner_id })} />
      ), { edit: true });
      case 'gpp': return cell(c, (
        <EditableCell type="select" fill
          value={r.referral_is_gpp ? 'yes' : 'no'}
          options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]}
          displayAs={r.referral_is_gpp ? <span>Yes</span> : <span style={{ color: 'var(--text-4)' }}>—</span>}
          locked={r.dc_synced}
          onSave={(v) => onSaveContact({ referral_is_gpp: v === 'yes' })} />
      ), { edit: true, title: 'GPP = the referral came from a Growth Partners Program partner' });
      case 'stage': return cell(c, r.stage_name ? `${r.stage_name}${r.on_hold ? ' · on hold' : ''}` : '—', {
        onClick: onOpenDeal,
        title: r.probability_pct != null && r.stage_name
          ? `${r.engagement_name || 'Engagement'} — ${Math.round(r.probability_pct)}% probability of closing`
          : 'No engagement yet for this company',
      });
      case 'next_action': return onSaveEngagement ? cell(c, (
        <EditableCell type="text" fill value={r.next_action_notes}
          locked={r.engagement_dc_synced}
          displayAs={nextAction ? <span>{nextAction}</span> : undefined}
          onSave={(next_action_notes) => onSaveEngagement({ next_action_notes })} />
      ), { edit: true, title: nextActionFull || undefined })
        : cell(c, '—', { title: 'No engagement yet for this company' });
      case 'fees': return cell(c, r.estimated_fees != null ? window.fmtMoney(r.estimated_fees) : '—');
      case 'revenue': return cell(c, r.revenue_est != null ? window.fmtMoney(r.revenue_est) : '—');
      case 'ebitda': return cell(c, r.ebitda_est != null ? window.fmtMoney(r.ebitda_est) : '—');
      case 'industry': return cell(c, r.industry_name || '—');
      case 'lead_source': return cell(c, r.lead_source || '—');
      case 'created': return cell(c, <window.RecordDateCell row={r} kind="created" inline />);
      case 'updated': return cell(c, <window.RecordDateCell row={r} kind="modified" inline />);
      default: return cell(c, '—');
    }
  };

  return (
    <div
      onClick={onOpenContact}
      onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-2)'}
      onMouseLeave={(e) => e.currentTarget.style.background = 'var(--surface)'}
      style={{ display: 'flex', minHeight: 34, alignItems: 'stretch',
               borderBottom: '1px solid var(--border)', background: 'var(--surface)',
               cursor: 'pointer' }}
    >
      {cols.map(renderCell)}
    </div>
  );
};

window.ReferralsView = ReferralsView;
window.ContactPicker = ContactPicker;
window.NewReferralDrawer = NewReferralDrawer;
